import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiMessageCircle, FiPhone, FiVideo, FiArrowRight, FiCheck, FiLayers, FiBarChart2, FiUsers, FiCpu, FiRefreshCw, FiTrendingUp, FiDollarSign, FiSettings
} from 'react-icons/fi';
import { FaCar } from 'react-icons/fa';
import logo from '../../Images/logo.png';
import styles from './SalesOperationsPage.module.css';

const SalesOperationsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.container}>
      {/* Ambient glows */}
      <div className={styles.ambientGlows}>
        <div className={styles.glow1}></div>
        <div className={styles.glow2}></div>
        <div className={styles.glow3}></div>
      </div>

      {/* 🟦 1. HEADER / NAVIGATION */}
      <header className={styles.header} style={{ flex: "none" }}>
        <div className={styles.headerInner}>
          <div className={styles.logoArea} onClick={() => navigate('/')}>
            <img src={logo} alt="SaleSense Logo" className={styles.logoImg} />
            <span className={styles.logoText}>Sailsense <span className={styles.logoCyan}>AI</span></span>
          </div>
          <nav className={styles.navLinks}>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <a href="/ai-agents" onClick={(e) => { e.preventDefault(); navigate('/ai-agents'); }}>AI Agents</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); navigate('/platform'); }}>Platform</a>
            <a href="/sales-operations" className={styles.activeLink} onClick={(e) => { e.preventDefault(); }}>Sales Operations</a>
            <a href="/solutions" onClick={(e) => { e.preventDefault(); navigate('/solutions'); }}>Solutions</a>
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
          </nav>
          <div className={styles.headerActions}>
            <button className={styles.trialBtn} onClick={() => navigate('/login')}>
              login <FiArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* Grid line overlay */}
      <div className={styles.gridOverlay}></div>

      {/* 🟦 2. SALES OPERATIONS INTRO */}
      <section className={styles.sectionBlock}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            Sales Operations
          </div>
          <h1 className={styles.sectionCenterTitle}>Turn Leads Into Customers Faster.</h1>
          <p className={styles.sectionSubtitle}>
            Purpose-built tools for dealerships and sales-driven businesses.
          </p>
        </div>

        <div className={styles.opsGrid}>
          {/* Inventory Management */}
          <div className={styles.opsCard}>
            <div className={styles.opsCardHeader}>
              <div className={styles.opsIconBox}>
                <FaCar />
              </div>
              <h4 className={styles.opsTitle}>
                Inventory Management
              </h4>
            </div>
            <p className={styles.opsDesc}>
              Manage vehicle inventory and availability from one platform.
            </p>
            <ul className={styles.opsList}>
              <li><span className={styles.cyanSquareBullet}></span> Vehicle Listings</li>
              <li><span className={styles.cyanSquareBullet}></span> Inventory Updates</li>
              <li><span className={styles.cyanSquareBullet}></span> Search & Filtering</li>
              <li><span className={styles.cyanSquareBullet}></span> Stock Management</li>
            </ul>
          </div>

          {/* Customer CRM */}
          <div className={styles.opsCard}>
            <div className={styles.opsCardHeader}>
              <div className={styles.opsIconBox}>
                <FiUsers />
              </div>
              <h4 className={styles.opsTitle}>
                Customer CRM
              </h4>
            </div>
            <p className={styles.opsDesc}>
              Maintain complete visibility of every customer interaction.
            </p>
            <ul className={styles.opsList}>
              <li><span className={styles.cyanSquareBullet}></span> Lead Database</li>
              <li><span className={styles.cyanSquareBullet}></span> Communication History</li>
              <li><span className={styles.cyanSquareBullet}></span> Customer Profiles</li>
              <li><span className={styles.cyanSquareBullet}></span> Follow-up Tracking</li>
            </ul>
          </div>

          {/* Deal Pipeline */}
          <div className={styles.opsCard}>
            <div className={styles.opsCardHeader}>
              <div className={styles.opsIconBox}>
                <FiTrendingUp />
              </div>
              <h4 className={styles.opsTitle}>
                Deal Pipeline
              </h4>
            </div>
            <p className={styles.opsDesc}>
              Monitor every stage of the sales journey.
            </p>
            <ul className={styles.opsList}>
              <li><span className={styles.cyanSquareBullet}></span> Opportunity Tracking</li>
              <li><span className={styles.cyanSquareBullet}></span> Test Drive Management</li>
              <li><span className={styles.cyanSquareBullet}></span> Deal Progress Monitoring</li>
              <li><span className={styles.cyanSquareBullet}></span> Contract Tracking</li>
            </ul>
          </div>

          {/* Finance & Insurance (F&I) */}
          <div className={styles.opsCard}>
            <div className={styles.opsCardHeader}>
              <div className={styles.opsIconBox}>
                <FiDollarSign />
              </div>
              <h4 className={styles.opsTitle}>
                Finance & Insurance (F&I)
              </h4>
            </div>
            <p className={styles.opsDesc}>
              Simplify financing and insurance workflows.
            </p>
            <ul className={styles.opsList}>
              <li><span className={styles.cyanSquareBullet}></span> Loan Calculations</li>
              <li><span className={styles.cyanSquareBullet}></span> Insurance Options</li>
              <li><span className={styles.cyanSquareBullet}></span> Financing Applications</li>
              <li><span className={styles.cyanSquareBullet}></span> Approval Tracking</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 🟦 3. LIFECYCLE SECTION */}
      <section className={styles.sectionBlock} style={{ borderTop: "1px solid rgba(255, 255, 255, 0.05)" }}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            Lifecycle
          </div>
          <h2 className={styles.sectionCenterTitle}>From inquiry to delivery — automated.</h2>
        </div>

        <div className={styles.lifecycleRow}>
          <div className={styles.lifecycleCard}>
            <span className={styles.stageLabel}>STAGE 1</span>
            <h4 className={styles.lifecycleTitleText}>Lead</h4>
          </div>
          <div className={styles.lifecycleCard}>
            <span className={styles.stageLabel}>STAGE 2</span>
            <h4 className={styles.lifecycleTitleText}>Qualify</h4>
          </div>
          <div className={styles.lifecycleCard}>
            <span className={styles.stageLabel}>STAGE 3</span>
            <h4 className={styles.lifecycleTitleText}>Test Drive</h4>
          </div>
          <div className={styles.lifecycleCard}>
            <span className={styles.stageLabel}>STAGE 4</span>
            <h4 className={styles.lifecycleTitleText}>Negotiation</h4>
          </div>
          <div className={styles.lifecycleCard}>
            <span className={styles.stageLabel}>STAGE 5</span>
            <h4 className={styles.lifecycleTitleText}>Delivered</h4>
          </div>
        </div>

        {/* Deploy AI sales team CTA */}
        <div className={styles.deployCtaBox}>
          <h3 className={styles.deployCtaHeading}>Run your dealership on Sailsense.</h3>
          <div className={styles.deployCtaButtons}>
            <button className={styles.heroBtnCyan} onClick={() => navigate('/login')}>
              Talk to Sales <FiArrowRight />
            </button>
          </div>
        </div>
      </section>

      {/* 🟦 4. FOOTER */}
      <footer className={styles.footerSection}>
        <div className={styles.footerInner}>
          {/* Brand area left */}
          <div className={styles.footerBrandColumn}>
            <div className={styles.footerBrandLogo} onClick={() => navigate('/')}>
              <img src={logo} alt="SaleSense Logo" className={styles.footerLogoImg} />
              <span>Sailsense AI</span>
            </div>
            <p className={styles.footerBrandText}>
              AI Chat, Voice and Video agents that capture leads, automate conversations and close sales — 24/7.
            </p>
            <p className={styles.footerBrandProductOf}>
              A product of <strong>Provaantech</strong>
            </p>
          </div>

          {/* Brand System center */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Brand System</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>
                <FiMessageCircle style={{ marginRight: '0.5rem' }} /> Sailsense Chatbot
              </li>
              <li className={styles.footerLinkItem}>
                <FiPhone style={{ marginRight: '0.5rem' }} /> ROVIA — Voice AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiVideo style={{ marginRight: '0.5rem' }} /> AIMEE — Video AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiSettings style={{ marginRight: '0.5rem' }} /> Sailsense Platform
              </li>
            </ul>
          </div>

          {/* Industries right */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Focus Industry</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>Automotive Dealerships</li>
            </ul>
          </div>
        </div>

        {/* Bottom row copyright */}
        <div className={styles.footerBottomBar}>
          <div>© 2026 Sailsense AI - Provaantech. All rights reserved.</div>
          <div>Built for high-performance sales teams.</div>
        </div>
      </footer>
    </div>
  );
};

export default SalesOperationsPage;
